import { clamp, invertMat3, transformMat3 } from "@/core/utils/math"
import { adaptionMatrixBradford, adaptionMatrixBradfordInv } from "@/core/utils/color"
import { FaceBox, FaceDetectionResult, Rect } from "@/lib/ai/detection/face-detection"
import { PixelData } from "@/core/utils/image-data"
import { mat3, vec2, vec3 } from "gl-matrix"
import { Adjustments, Crop, RadialMask, Rectangle } from "@/core/types"
import { ArrayData, bilinearInterpolation } from "@/lib/ai/alignment/ai-line-detection"
import { equals, l2Distance, rectCenter } from "@/utils/math"
import { logAiInfo } from "../utils"
import equal from "fast-deep-equal"
import { getDefaultAdjustments } from "@/core/adjustments"

// prettier-ignore
const RGB_TO_XYZ: mat3 = [
  0.412453, 0.357580, 0.180423,
  0.212671, 0.715160, 0.072169,
  0.019334, 0.119193, 0.950227
]

// prettier-ignore
const XYZ_TO_RGB: mat3 = [
  3.240481, -1.537151, -0.498536,
  -0.969256, 1.875990, 0.0415560,
  0.055647, -0.204041, 1.057311
]

// prettier-ignore
const RGB_TO_YUV: mat3 = [
  0.299, 0.587, 0.114,
  -0.299, -0.587, 0.886,
  0.701, -0.587, -0.114
]

const YUV_TO_RGB: mat3 = invertMat3(RGB_TO_YUV)

export function getMean(data: number[] | Float32Array) {
	let total = 0

	for (const element of data) {
		total += element
	}

	return total / Math.max(1, data.length)
}

export function getStandardDeviation(data: number[], mean: number | undefined = undefined) {
	const meanData = mean ?? getMean(data)
	let sumSq = 0
	for (const d of data) {
		sumSq += (d - meanData) * (d - meanData)
	}
	return Math.sqrt(sumSq / Math.max(1, data.length))
}

export function getMeanV3(data: vec3[]): vec3 {
	const total: vec3 = [0, 0, 0]

	for (const element of data) {
		total[0] += element[0]
		total[1] += element[1]
		total[2] += element[2]
	}

	return [total[0] / data.length, total[1] / data.length, total[2] / data.length]
}

export function percentDifference(
	a: number | undefined | null,
	b: number | undefined | null,
	defaultDiff = 0.3,
) {
	if (a == null || b == null) {
		return defaultDiff
	}
	return Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1e-4)
}

export function percentRatioToMin(
	a: number | undefined | null,
	b: number | undefined | null,
	defaultDiff = 0.3,
) {
	if (a == null || b == null) {
		return defaultDiff
	}
	return Math.abs(a - b) / Math.max(0.000001, Math.min(Math.abs(a), Math.abs(b)))
}

export function absoluteDifference(a, b, defaultDiff = 0.3) {
	if (a == null || b == null) {
		return defaultDiff
	}
	return Math.abs(Number(a) - Number(b))
}

export function normalizeBySum(vector: number[]) {
	const sum = vector.reduce((total, currentValue) => total + currentValue, 0)
	return vector.map((component) => component / sum)
}

export function meanAbsoluteDifference(vector1: number[], vector2: number[], maxDist = 1) {
	if (vector1.length !== vector2.length) {
		return maxDist // max distance
	}

	let sum = 0
	for (let i = 0; i < vector1.length; i++) {
		const difference = Math.abs((vector1[i] as number) - (vector2[i] as number))
		sum += difference
	}

	return sum / Math.max(1, vector1.length)
}

export function extractKeys<T extends Record<string, any>, K extends keyof T>(
	source: T,
	keysToExtract: readonly K[],
) {
	const result: Pick<T, K> = {} as Pick<T, K>

	for (const key of keysToExtract) {
		if (Object.hasOwn(source, key)) {
			result[key] = source[key]
		}
	}

	return result
}

export function are2DArraysEqual(a, b) {
	if (!a || !b) {
		return !a && !b
	}

	for (let i = 0; i < a.length; i++) {
		if (a[i].length !== b[i].length) return false

		for (let j = 0; j < a[i].length; j++) {
			if (a[i][j] !== b[i][j]) return false
		}
	}

	return true
}

export function isCropEqual(crop1: Crop, crop2: Crop) {
	const dx = Math.abs(crop1[0] - crop2[0])
	const dy = Math.abs(crop1[1] - crop2[1])
	const dw = Math.abs(crop1[2] - crop2[2])
	const dh = Math.abs(crop1[3] - crop2[3])

	if (dx < 0.008 && dy < 0.008 && dw < 0.002 && dh < 0.002) return true
	else return false
}

export function isEmpty(obj: object | null | undefined) {
	return obj === null || obj === undefined || Object.keys(obj).length === 0
}

export function srgb_to_linear(rgb: vec3) {
	return rgb.map((x) => (x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4))) as vec3
}

export function clip(arr: vec3, min: number, max: number) {
	return arr.map((num) => Math.min(Math.max(num, min), max)) as vec3
}

export function linear_to_srgb(rgb: vec3): vec3 {
	const out = rgb.map((x) =>
		x <= 0.0031308 ? x * 12.92 : 1.055 * Math.pow(x, 1.0 / 2.4) - 0.055,
	) as vec3
	return clip(out, 0, 1)
}

export function srgb_to_xyz(rgb: vec3) {
	return transformMat3(srgb_to_linear(rgb), RGB_TO_XYZ)
}

export function xyz_to_srgb(xyz: vec3) {
	return linear_to_srgb(transformMat3(xyz, XYZ_TO_RGB))
}

export function rgb_to_yuv(rgb: vec3) {
	return transformMat3(rgb, RGB_TO_YUV)
}

export function yuv_to_rgb(yuv: vec3) {
	return transformMat3(yuv, YUV_TO_RGB)
}

export function xyz_to_lms(xyz: vec3) {
	return transformMat3(normalize_xyz(xyz), adaptionMatrixBradford)
}

export function lms_to_xyz(lms: vec3) {
	return transformMat3(lms, adaptionMatrixBradfordInv)
}

/**
 * Normalize an XYZ vector by its luminance (Y) so the result represents pure
 * chromaticity (Y == 1). Used as the first step of `xyz_to_lms` for chromatic
 * adaptation in both auto-WB and color-match WB flows.
 *
 * Caller must ensure `vec[1]` (Y) > 0 and all components finite. When Y === 0
 * (e.g. `srgb_to_xyz([0, 0, 0])` from a corrupted all-black render) the result
 * is `[NaN, 1, NaN]` and NaN propagates through the WB pipeline. The boundary
 * guards in `getAutoWBFromIlluminant` / `getWhiteBlanceParams` report to Sentry
 * on the way out, and `image-processing.ts` handles the user-visible fallback / error.
 */
export function normalize_xyz(vec: vec3): vec3 {
	const x = vec[0]
	const y = vec[1]
	const z = vec[2]
	return [x / y, 1, z / y]
}

export function shapeRGBA(rgba: ArrayLike<number>) {
	const result: vec3[] = []

	for (let i = 0; i < rgba.length; i += 4) {
		result.push([
			<number>rgba[i + 0] / 255,
			<number>rgba[i + 1] / 255,
			<number>rgba[i + 2] / 255,
		])
	}

	return result
}

export function getMaskedPixels(
	imageData: PixelData | ImageData,
	faceDetectionResults: FaceDetectionResult[],
	useStep = true,
): { maskedPercent: number; maskedPixels: number[] } {
	const data = imageData.data
	const imageW = imageData.width

	let filteredFaceDetectionResults = faceDetectionResults.filter((val) => val.skinCondition)
	let hasMask = true
	if (filteredFaceDetectionResults.length === 0) {
		filteredFaceDetectionResults = faceDetectionResults
		hasMask = false
	}

	// --- OPTIMIZATION 1: Pre-calculate step to avoid building a large intermediate array ---
	let step = 1
	if (useStep) {
		let estimatedTotalPixels = 0
		for (const face of filteredFaceDetectionResults) {
			const box = face.faceBox
			estimatedTotalPixels +=
				(box[2] - box[0]) * (box[3] - box[1]) * (hasMask ? (face.skinPercent ?? 0.25) : 1)
		}

		const calculatedStep = Math.round(Math.sqrt(estimatedTotalPixels / 10000))
		step = Math.min(Math.max(calculatedStep, 1), 4)
	}
	// console.log("face masked pixels step, w, h", step, imageW, imageData.height)

	const result: number[] = []

	for (const face of filteredFaceDetectionResults) {
		const box = face.faceBox
		const mask = face.skinCondition
		const cropH = box[3] - box[1]
		const cropW = box[2] - box[0]
		const x1 = box[0]
		const y1 = box[1]

		// --- OPTIMIZATION 2: Use step directly in the loop for grid-based sampling ---
		for (let i = 0; i < cropH; i += step) {
			const maskRow = mask?.[i]
			for (let j = 0; j < cropW; j += step) {
				// The condition is true if there's no mask OR if the mask at this position is true.
				if (!mask || maskRow?.[j]) {
					const ii = (j + x1 + (i + y1) * imageW) * 4
					result.push(
						data[ii] as number,
						data[ii + 1] as number,
						data[ii + 2] as number,
						data[ii + 3] as number,
					)
				}
			}
		}
	}

	// NOTE: This percentage is now an *estimate* based on the sampled pixels.
	// We multiply by step*step to approximate the total number of pixels that would have been found.
	const estimatedTotalMaskedPixels = (result.length / 4) * step * step
	const maskedPercent =
		estimatedTotalMaskedPixels > 0 ? (estimatedTotalMaskedPixels * 4) / data.length : 0

	return { maskedPercent, maskedPixels: result }
}

function l2Norm(vector: number[] | Float32Array) {
	let sumOfSquares = 0
	for (const element of vector) {
		sumOfSquares += element * element
	}
	return Math.sqrt(sumOfSquares)
}

export function normalize(vector: number[]) {
	const norm = Math.max(l2Norm(vector), 0.000001)
	return vector.map((component) => component / norm)
}

export function normalizeFloatArray(vector: Float32Array) {
	const norm = Math.max(l2Norm(vector), 0.000001)
	const result = new Float32Array(vector.length)
	for (let i = 0; i < vector.length; i++) {
		result[i] = (vector[i] as number) / norm
	}
	return result
}

export function normalizeFaceBox(faceBox: FaceBox, width: number, height: number): Rect {
	return {
		x: faceBox[0] / width,
		y: faceBox[1] / height,
		width: (faceBox[2] - faceBox[0]) / width,
		height: (faceBox[3] - faceBox[1]) / height,
	}
}

export function faceRectToBox(faceRect: Rect, srcWidth: number, srcHeight: number): FaceBox {
	const { x, y, width, height } = faceRect
	const x1 = clamp(Math.round(x * srcWidth), 0, srcWidth - 1)
	const x2 = clamp(Math.round(x1 + width * srcWidth), 0, srcWidth - 1)
	const y1 = clamp(Math.round(y * srcHeight), 0, srcHeight - 1)
	const y2 = clamp(Math.round(y1 + height * srcHeight), 0, srcHeight - 1)
	return [x1, y1, x2, y2]
}

export function getAllFaceBounds(faceBoxCoords: Rect[]): Rect {
	let minX = 1,
		minY = 1,
		maxX = 0,
		maxY = 0

	for (const coord of faceBoxCoords) {
		minX = Math.min(minX, coord.x)
		minY = Math.min(minY, coord.y)
		maxX = Math.max(maxX, coord.x + coord.width)
		maxY = Math.max(maxY, coord.y + coord.height)
	}

	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

export function getRectCenter(faceBoxCoord: Rect): [number, number] {
	return [faceBoxCoord.x + faceBoxCoord.width / 2, faceBoxCoord.y + faceBoxCoord.height / 2]
}

export function getFaceArea(faceBoxCoord: Rect): number {
	return faceBoxCoord.width * faceBoxCoord.height
}

export function isRectCoveredByCrop(rect: Rect, crop: Crop) {
	return (
		rect.x >= crop[0] &&
		rect.y >= crop[1] &&
		rect.x + rect.width <= crop[0] + crop[2] &&
		rect.y + rect.height <= crop[1] + crop[3]
	)
}

export function getSlightlyPaddedFaceRect(
	rect: Rect,
	imgSize: vec2,
	maxRatio: number = 1.25,
	maxPadding: number = 20,
) {
	const paddedRect = { ...rect }

	const deltaX = 0.2 * rect.width
	const deltaYTop = 0.25 * rect.height
	const deltaYBottom = 0.15 * rect.height
	paddedRect.x = Math.max(rect.x - deltaX, 0)
	paddedRect.y = Math.max(rect.y - deltaYTop, 0)
	paddedRect.width = Math.min(rect.x + rect.width + deltaX, 1) - paddedRect.x
	// pad less bottom to move the center upwards
	paddedRect.height = Math.min(rect.y + rect.height + deltaYBottom, 1) - paddedRect.y

	let result = padWidthToRatio(paddedRect, imgSize, maxRatio)

	// If face resolution is too small, pad a little more to improve accuracy
	const size = Math.min(rect.width * imgSize[0], rect.height * imgSize[1])
	const targetSize = 80
	if (size < targetSize && maxPadding > 0) {
		result = padToTargetSize({ rect: result, imgSize, targetSize, maxPadding })
	}

	return result
}

export function padToTargetSize(options: {
	rect: Rect
	imgSize: vec2
	targetSize: number
	maxPadding: number
}) {
	const { rect, imgSize, targetSize, maxPadding } = options
	const [w, h] = imgSize
	const size = Math.min(rect.width * w, rect.height * h)
	const result = { ...rect }
	if (size < targetSize && maxPadding > 0) {
		const newSize = Math.min(targetSize, size + maxPadding, size * 2)
		const deltaX = Math.max(0, newSize - rect.width * imgSize[0]) / 2 / imgSize[0]
		const deltaY = Math.max(0, newSize - rect.height * imgSize[1]) / 2 / imgSize[1]
		result.x = Math.max(rect.x - deltaX, 0)
		result.y = Math.max(rect.y - deltaY, 0)
		result.width = Math.min(rect.x + rect.width + deltaX, 1) - result.x
		result.height = Math.min(rect.y + rect.height + deltaY, 1) - result.y

		logAiInfo("padToTargetSize", size, "->", newSize)
	}
	return result
}

// pad side face to be closer to square
export function padWidthToRatio(rect: Rect, imgSize: vec2, maxRatio: number) {
	const [w, h] = imgSize
	const rectW = w * rect.width
	const rectH = h * rect.height
	const result = { ...rect }

	if (rectH / rectW <= maxRatio) return result

	const newRectW = Math.round(rectH / maxRatio)
	const deltaX = Math.max(0, newRectW - rectW) / 2 / w

	result.x = Math.max(rect.x - deltaX, 0)
	result.width = Math.min(rect.x + rect.width + deltaX, 1) - rect.x

	return result
}

export function shrinkFaceWidth(faceBoxCoord: Rect, scale = 1.2): Rect {
	return {
		x: faceBoxCoord.x + (faceBoxCoord.width * (1 - 1 / scale)) / 2,
		y: faceBoxCoord.y,
		width: faceBoxCoord.width / scale,
		height: faceBoxCoord.height,
	}
}

export function getShrinkedFaces(faceCoords: Rect[], scale = 1.2): Rect[] {
	const shrinkedFaces: Rect[] = []
	for (const face of faceCoords) {
		shrinkedFaces.push(shrinkFaceWidth(face, scale))
	}
	return shrinkedFaces
}

export function getMatchingFaceIndex(options: {
	targetRect: Rect
	faceBoxCoords: Rect[]
	dThresh?: number
	widthThresh?: number
	widthThreshAllFaces?: number
	checkY?: boolean
	includeAllFaceRect?: boolean
}): number {
	const { targetRect, faceBoxCoords } = options
	const dThresh = options.dThresh ?? 0.05
	const widthThresh = options.widthThresh ?? 0.7
	const widthThreshAllFaces = options.widthThreshAllFaces ?? 0.35
	const checkY = options.checkY ?? false
	const includeAllFaceRect = options.includeAllFaceRect ?? true

	let allFaceRects = faceBoxCoords
	if (faceBoxCoords.length > 1 && includeAllFaceRect) {
		const allFaceBounds = getAllFaceBounds(faceBoxCoords)
		allFaceRects = faceBoxCoords.concat(allFaceBounds)
	}

	const maskCenter = getRectCenter(targetRect)
	const widthDiffWeight = 0.4
	const distMap: Record<number, number> = {}
	for (let i = 0; i < allFaceRects.length; i++) {
		const rect = { ...(allFaceRects[i] as Rect) }

		const center = getRectCenter(rect)
		const dx = Math.abs(center[0] - maskCenter[0])
		const dy = Math.abs(center[1] - maskCenter[1])
		const dyTop = Math.abs(rect.y - targetRect.y)

		let currWidthThresh = widthThresh
		if (i === faceBoxCoords.length) {
			currWidthThresh = widthThreshAllFaces
		}
		const widthDiff = Math.abs(rect.width / targetRect.width - 1)
		const dist = Math.sqrt(dx * dx + dy * dy) + widthDiff * widthDiffWeight
		// logAiInfo("widthDiff", { targetRect, rect, widthDiff, dx, dy, dist })

		if (checkY) {
			if (dx < dThresh && dy < dThresh && widthDiff < currWidthThresh) {
				distMap[i] = dist
			}
		} else if ((dx < dThresh || dx + dyTop < 2 * dThresh) && widthDiff < currWidthThresh) {
			distMap[i] = dist
		}
	}

	const sortedDists = Object.entries(distMap).sort((a, b) => a[1] - b[1])
	if (sortedDists.length > 0) {
		const sortedDist0 = sortedDists[0] as [string, number]
		return parseInt(sortedDist0[0])
	}

	return -1 // no face in image or not matched
}

// ignore angle
export function radialMaskToRect(mask: RadialMask) {
	const maskRect: Rect = {
		x: mask.centerX - mask.radiusX,
		y: mask.centerY - mask.radiusY,
		width: mask.radiusX * 2,
		height: mask.radiusY * 2,
	}
	return maskRect
}

export function getMaskPercent(maskData: Float32Array, step: number = 2) {
	let pixelCount = 0
	for (let i = 0; i < maskData.length; i += step) {
		if (<number>maskData[i] > 0.5) pixelCount += 1
	}

	return (pixelCount * step) / maskData.length
}

export function getCoordsRange(rect: Rectangle, srcSize: vec2) {
	const [width, height] = srcSize

	const startX = clamp(Math.round(rect.x * width), 0, width - 1)
	const endX = clamp(startX + Math.round(rect.width * width), startX + 1, width)

	const startY = clamp(Math.round(rect.y * height), 0, height - 1)
	const endY = clamp(startY + Math.round(rect.height * height), startY + 1, height)

	return { startX, endX, startY, endY }
}

export function getAbsDiffForImagePatch(
	gray: ArrayData,
	keyGray: ArrayData,
	rect: Rectangle,
	offset: vec2 = [0, 0],
	aveThresh = 1.5,
) {
	const { startX, endX, startY, endY } = getCoordsRange(rect, [gray.width, gray.height])

	const dx = Math.round(offset[0] * gray.width)
	const dy = Math.round(offset[1] * gray.height)
	let aveDiff = 0
	const diffs: number[] = []
	for (let x = startX; x < endX; x++) {
		for (let y = startY; y < endY; y++) {
			const index = y * gray.width + x
			const indexKey = (y + dy) * gray.width + x + dx
			const diff = (gray.data[index] || 0) - (keyGray.data[indexKey] || 0)
			aveDiff += Math.abs(diff)
			diffs.push(diff)
		}
	}
	if (diffs.length === 0) return { aveDiff: 0, stdDiff: 0 }

	aveDiff /= diffs.length

	if (aveDiff < aveThresh) {
		return { aveDiff, stdDiff: 0 }
	} else {
		const stdDiff = getStandardDeviation(diffs)
		return { aveDiff, stdDiff }
	}
}

export function getCenterOffset(rect: Rectangle, refRect: Rectangle): vec2 {
	const center = rectCenter(rect)
	const center2 = rectCenter(refRect)
	return [center[0] - center2[0], center[1] - center2[1]]
}

export function getAbsDiffForDifferentRects(
	gray: ArrayData,
	keyGray: ArrayData,
	rect: Rectangle,
	keyRect: Rectangle,
	aveThresh = 1.5,
) {
	const w1 = rect.width * gray.width
	const h1 = rect.height * gray.height
	const w2 = keyRect.width * gray.width
	const h2 = keyRect.height * gray.height

	// size almost equal, no need to resize
	if (equals(w1 / w2, 1, 0.02) && equals(h1 / h2, 1, 0.02)) {
		const offset = getCenterOffset(keyRect, rect)
		const res = getAbsDiffForImagePatch(gray, keyGray, rect, offset)
		const histDiff = res.aveDiff > aveThresh ? getHistogramDiff(gray, keyGray) : 0
		return { ...res, histDiff }
	}

	const patch = getImagePatch(gray, rect)
	const keyPatch = getImagePatch(keyGray, keyRect)
	if (patch.width * patch.height < keyPatch.width * keyPatch.height) {
		const resizedKeyPatch = bilinearInterpolation(keyPatch, patch.width, patch.height)
		const res = getAbsDiffForSameSizedPatch(patch, resizedKeyPatch, aveThresh)
		return { ...res }
	} else {
		const resizedPatch = bilinearInterpolation(patch, keyPatch.width, keyPatch.height)
		const res = getAbsDiffForSameSizedPatch(resizedPatch, keyPatch, aveThresh)
		return { ...res }
	}
}

export function getImagePatch(gray: ArrayData, rect: Rectangle): ArrayData {
	const { startX, endX, startY, endY } = getCoordsRange(rect, [gray.width, gray.height])

	const width = endX - startX
	const height = endY - startY
	const data: number[] = []
	for (let y = startY; y < endY; y++) {
		for (let x = startX; x < endX; x++) {
			const index = y * gray.width + x
			data.push(gray.data[index] as number)
		}
	}

	return { data, width, height }
}

export function getRectBoundaryIndices(
	rect: Rectangle,
	width: number,
	height: number,
	offset: vec2 = [0, 0],
	padding = 0.0,
) {
	const startX = Math.round(rect.x * width) + offset[0]
	let endX = startX + Math.round(rect.width * width)
	if (offset[0] === 0) endX = clamp(endX, 0, width)

	if (startX / width < padding || endX / width > 1 - padding) return null

	const startY = Math.round(rect.y * height) + offset[1]
	let endY = startY + Math.round(rect.height * height)
	if (offset[1] === 0) endY = clamp(endY, 0, height)

	if (startY / height < padding || endY / height > 1 - padding) return null

	const boundaryIndices: number[] = []
	for (let x = startX; x < endX; x++) {
		boundaryIndices.push(startY * width + x)
	}
	for (let x = startX; x < endX; x++) {
		boundaryIndices.push((endY - 1) * width + x)
	}
	for (let y = startY; y < endY; y++) {
		boundaryIndices.push(y * width + startX)
	}
	for (let y = startY; y < endY; y++) {
		boundaryIndices.push(y * width + endX - 1)
	}

	return boundaryIndices
}

export function getAbsDiffForRectBounds(
	gray: ArrayData,
	boundaryIndices1: number[],
	boundaryIndices2: number[],
) {
	const n = Math.min(boundaryIndices1.length, boundaryIndices2.length)
	let aveDiff = 0
	for (let i = 0; i < n; i++) {
		const idx1 = boundaryIndices1[i] as number
		const idx2 = boundaryIndices2[i] as number
		const diff = Math.abs((gray.data[idx1] as number) - (gray.data[idx2] as number))
		aveDiff += diff
	}
	aveDiff /= Math.max(0, n)

	return aveDiff
}

export function getAbsDiffForSameSizedPatch(gray1: ArrayData, gray2: ArrayData, aveThresh: number) {
	const n = Math.min(gray1.data.length, gray2.data.length)
	let aveDiff = 0
	const diffs: number[] = []
	for (let i = 0; i < n; i++) {
		const diff = (gray1.data[i] || 0) - (gray2.data[i] || 0)
		aveDiff += Math.abs(diff)
		diffs.push(diff)
	}
	aveDiff /= n

	if (aveDiff < aveThresh) {
		return { aveDiff, stdDiff: 0, histDiff: 0 }
	} else {
		const stdDiff = getStandardDeviation(diffs)
		const histDiff = getHistogramDiff(gray1, gray2)
		return { aveDiff, stdDiff, histDiff }
	}
}

function getHistogram(gray: ArrayData, numBins = 128, maxNum = 256): number[] {
	const bins = new Array<number>(numBins).fill(0)
	const data = gray.data
	const percent = 1.0 / data.length

	// Calculate histogram
	for (const element of data) {
		const binIndex = Math.min(Math.floor((element / maxNum) * numBins), numBins - 1)
		if (bins[binIndex]) bins[binIndex] += percent
	}

	return bins
}

function getHistogramDiff(gray1: ArrayData, gray2: ArrayData) {
	const hist1 = getHistogram(gray1)
	const hist2 = getHistogram(gray2)

	return l2Distance(normalize(hist1), normalize(hist2))
}

export function isRectAtEdge(rect: Rectangle) {
	if (
		equals(rect.x, 0) ||
		equals(rect.x + rect.width, 1) ||
		equals(rect.y, 0) ||
		equals(rect.y + rect.height, 1)
	) {
		return true
	}
	return false
}

export function rectOverlapRatio(rect1: Rectangle, rect2: Rectangle) {
	const [xMin1, xMax1, yMin1, yMax1] = [
		rect1.x,
		rect1.x + rect1.width,
		rect1.y,
		rect1.y + rect1.height,
	]
	const [xMin2, xMax2, yMin2, yMax2] = [
		rect2.x,
		rect2.x + rect2.width,
		rect2.y,
		rect2.y + rect2.height,
	]

	// Calculate overlap width and height
	const width = Math.min(xMax1, xMax2) - Math.max(xMin1, xMin2)
	const height = Math.min(yMax1, yMax2) - Math.max(yMin1, yMin2)

	return (
		(Math.max(0, width * height) * 2) /
		(rect1.width * rect1.height + rect2.width * rect2.height)
	)
}

export function isPresetAlmostEqual(
	preset1: Partial<Adjustments>,
	preset2Full: Adjustments,
	isAppliedPreset: boolean = false,
) {
	// use different thresholds for different adjustment keys
	const strictKeys = [
		"curvesAll",
		"curvesRed",
		"curvesGreen",
		"curvesBlue",
		"pointColors",
		"calibrationRedHue",
		"calibrationRedSat",
		"calibrationGreenHue",
		"calibrationGreenSat",
		"calibrationBlueHue",
		"calibrationBlueSat",
		"parametricDarksStop",
		"parametricMidsStop",
		"parametricLightsStop",
		"parametricHighlights",
		"parametricLights",
		"parametricDarks",
		"parametricShadows",
	]

	const looseKeys = ["highlights", "shadows", "whites", "blacks", "contrast"]

	const looseThreshold = 0.2 + (isAppliedPreset ? 0.1 : 0)
	const defaultThreshold = 0.06 + (isAppliedPreset ? 0.06 : 0)
	const strictThreshold = 0.03
	const absValueThreshold = 0.1 // to prevent matching to previous preset with too large absolute value (such as strong shadows/highlights)

	// Get all keys from both presets
	const defaultAdjustments = getDefaultAdjustments()
	defaultAdjustments.colorProfile = null
	preset1 = { ...defaultAdjustments, ...preset1 }
	const allKeys = new Set([
		...(Object.keys(preset1) as (keyof Adjustments)[]),
		...(Object.keys(preset2Full) as (keyof Adjustments)[]),
	])

	for (const key of allKeys) {
		if (key.includes("colorMatch")) continue

		const value1 = preset1[key]
		const value2 = preset2Full[key]

		// If one exists and the other doesn't, they're not equal
		if (
			(value1 === undefined || value1 === null) !== (value2 === undefined || value2 === null)
		) {
			logAiInfo("preset value not equal:", key, "=>", value1, value2)
			return false
		}

		// If both are undefined/null, continue
		if (
			(value1 === undefined || value1 === null) &&
			(value2 === undefined || value2 === null)
		) {
			continue
		}

		// At this point, both values are defined (not null/undefined)
		// For non-number values (curves, pointColors, arrays, objects, booleans), use strict equality
		if (typeof value1 !== "number" || typeof value2 !== "number") {
			if (!equal(value1, value2)) {
				logAiInfo("preset value not equal:", key, "=>", value1, value2)
				return false
			}
			continue
		}

		// For number values, use appropriate threshold
		let threshold: number
		if (strictKeys.includes(key)) {
			threshold = strictThreshold
		} else if (looseKeys.includes(key)) {
			threshold = looseThreshold
		} else {
			threshold = defaultThreshold
		}

		// Compare numbers with threshold (both are guaranteed to be numbers here)
		if (
			Math.abs(value1 - value2) > threshold ||
			(Math.abs(value1) - Math.abs(value2) > absValueThreshold && Math.abs(value1) > 0.3)
		) {
			logAiInfo("preset value not equal:", key, "=>", value1, value2, threshold)
			return false
		}
	}

	return true
}

/**
 * Calculate smart square crop coordinates based on face detection boxes
 * - If width > height (landscape): Use center of all faces X-coordinate as center for square crop
 * - If height > width (portrait): Use center of all faces Y-coordinate as 40% from top of square
 * - If no faces: Use center crop
 *
 * @param imageSize - [width, height] in pixels
 * @param faceBoxCoords - Array of face rectangles (normalized coordinates 0-1)
 * @returns Crop coordinates in pixels { x, y, size } or null if invalid
 */
export function getFaceBasedSquareCrop(
	imageSize: vec2,
	faceBoxCoords: Rect[],
): { x: number; y: number; size: number } | null {
	const [width, height] = imageSize
	const size = Math.min(width, height)

	if (size <= 0) return null

	// Default to center crop if no faces
	if (!faceBoxCoords || faceBoxCoords.length === 0) {
		return {
			x: Math.floor((width - size) / 2),
			y: Math.floor((height - size) / 2),
			size,
		}
	}

	// Get bounding box of all faces and its center
	const allFacesBounds = getAllFaceBounds(faceBoxCoords)
	const facesCenter = getRectCenter(allFacesBounds)

	// Convert normalized center to pixels
	const facesCenterX = facesCenter[0] * width
	const facesCenterY = facesCenter[1] * height

	let x: number
	let y: number

	if (width > height) {
		// Landscape: center crop horizontally around faces center X, full height
		const targetCenterX = facesCenterX
		x = Math.floor(targetCenterX - size / 2)
		// Clamp to valid range
		x = clamp(x, 0, width - size)
		y = 0
	} else if (height > width) {
		// Portrait: center crop vertically with faces center Y at 40% from top, full width
		const targetCenterY = facesCenterY
		y = Math.floor(targetCenterY - size * 0.4)
		// Clamp to valid range
		y = clamp(y, 0, height - size)
		x = 0
	} else {
		// Square: no cropping
		x = 0
		y = 0
	}

	return { x, y, size }
}

export function getNearlySquareCrop(imageSize: vec2): {
	x: number
	y: number
	w: number
	h: number
} {
	const [width, height] = imageSize

	let x: number = 0
	let y: number = 0
	let w: number = 0
	let h: number = 0

	if (width > height) {
		w = Math.max(Math.round(0.8 * width), height)
		h = height
		x = Math.floor((width - w) / 2)
		y = 0
	} else if (height > width) {
		h = Math.max(Math.round(0.8 * height), width)
		w = width
		y = Math.floor((height - h) / 2)
		x = 0
	} else {
		w = h = Math.min(width, height)
		x = y = 0
	}
	return { x, y, w, h }
}
